<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <EmployeeSidebar :sidebarOpen="sidebarOpen" currentPage="timesheets" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Navbar -->
      <EmployeeNavbar
        :title="$t('employee.timesheets.title')"
        @toggleSidebar="toggleSidebar"
        @logout="handleLogout"
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Inline feedback, so nothing blocks the flow like alert() did -->
        <div
          v-if="banner"
          class="alert alert-dismissible fade show"
          :class="`alert-${banner.type}`"
        >
          {{ banner.message }}
          <button type="button" class="btn-close" @click="banner = null"></button>
        </div>

        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-secondary">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">
                      {{ $t("employee.timesheets.draftTimesheets") }}
                    </p>
                    <h3 class="mb-0 fw-bold">{{ draftCount }}</h3>
                    <small class="text-muted">
                      {{ $t("employee.timesheets.notSubmitted") }}
                    </small>
                  </div>
                  <div class="stat-icon bg-secondary">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">
                      {{ $t("employee.timesheets.pendingApproval") }}
                    </p>
                    <h3 class="mb-0 fw-bold">{{ pendingCount }}</h3>
                    <small class="text-muted">
                      {{ pendingHours }}{{ $t("timesheets.hours") }}
                      {{ $t("timesheets.submitted").toLowerCase() }}
                    </small>
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"
                      />
                      <path
                        d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">
                      {{ $t("employee.timesheets.approvedTimesheets") }}
                    </p>
                    <h3 class="mb-0 fw-bold">{{ approvedHours }}h</h3>
                    <small class="text-muted">
                      {{ $t("employee.timesheets.thisMonth") }} ·
                      {{ formatCurrency(approvedAmount) }}
                    </small>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ================= Week view ================= -->
        <div class="card border-0 shadow-sm mb-4">
          <div
            class="card-header bg-white d-flex flex-wrap gap-2 justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center gap-2">
              <button
                class="btn btn-sm btn-outline-secondary"
                :title="$t('employee.timesheets.previousWeek')"
                @click="shiftWeek(-1)"
              >
                &lsaquo;
              </button>
              <div>
                <h5 class="mb-0">
                  {{
                    $t("employee.timesheets.weekOf", {
                      start: formatDateEU(week.weekStart),
                      end: formatDateEU(week.weekEnd),
                    })
                  }}
                </h5>
                <small v-if="isCurrentWeek" class="text-primary">
                  {{ $t("employee.timesheets.currentWeek") }}
                </small>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="isCurrentWeek"
                :title="$t('employee.timesheets.nextWeek')"
                @click="shiftWeek(1)"
              >
                &rsaquo;
              </button>
            </div>

            <button class="btn btn-primary" @click="openNewDay()">
              + {{ $t("employee.timesheets.logTodaysHours") }}
            </button>
          </div>

          <div class="card-body">
            <div v-if="loadingWeek" class="text-center py-4 text-muted">
              {{ $t("common.loading") }}
            </div>

            <div v-else class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>{{ $t("timesheets.day") }}</th>
                    <th>{{ $t("timesheets.date") }}</th>
                    <th class="text-end">{{ $t("timesheets.hoursLabel") }}</th>
                    <th>{{ $t("timesheets.dayStatus") }}</th>
                    <th class="text-end">{{ $t("common.actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="day in week.days"
                    :key="day.date"
                    :class="{ 'table-light': day.isToday }"
                  >
                    <td>
                      {{ $t("timesheets." + day.dayName.toLowerCase()) }}
                      <span v-if="day.isToday" class="badge bg-primary ms-1">
                        {{ $t("employee.timesheets.today") }}
                      </span>
                    </td>
                    <td>{{ formatDateEU(day.date) }}</td>
                    <td class="text-end">
                      <span v-if="day.entry">
                        {{ day.entry.hours }}{{ $t("timesheets.hours") }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span
                        v-if="day.entry"
                        class="badge"
                        :class="getStatusBadgeClass(day.entry.status)"
                      >
                        {{ $t("timesheets." + day.entry.status) }}
                      </span>
                      <span v-else-if="day.isFuture" class="text-muted small">
                        {{ $t("employee.timesheets.futureDay") }}
                      </span>
                      <span v-else class="text-muted small">
                        {{ $t("employee.timesheets.noEntry") }}
                      </span>

                      <!-- The admin's reason, shown where the fix happens -->
                      <div
                        v-if="day.entry && day.entry.rejection_reason"
                        class="small text-danger mt-1"
                      >
                        {{
                          $t("employee.timesheets.rejectedReason", {
                            reason: day.entry.rejection_reason,
                          })
                        }}
                      </div>
                    </td>
                    <td class="text-end">
                      <template v-if="!day.entry">
                        <button
                          v-if="!day.isFuture"
                          class="btn btn-sm btn-outline-primary"
                          @click="openNewDay(day.date)"
                        >
                          {{ $t("employee.timesheets.addHours") }}
                        </button>
                      </template>

                      <template v-else-if="isEditable(day.entry)">
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          @click="openEditDay(day.entry)"
                        >
                          {{
                            day.entry.status === "rejected"
                              ? $t("employee.timesheets.resubmit")
                              : $t("employee.timesheets.editHours")
                          }}
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteDay(day.entry)"
                        >
                          {{ $t("employee.timesheets.deleteEntry") }}
                        </button>
                      </template>

                      <span v-else class="text-muted small">
                        {{ $t("employee.timesheets.cannotEditSubmitted") }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="fw-semibold">
                    <td colspan="2">{{ $t("timesheets.total") }}</td>
                    <td class="text-end">
                      {{ week.totalHours }}{{ $t("timesheets.hours") }}
                    </td>
                    <td colspan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Week summary: where the week's hours currently stand -->
            <div v-if="week.timesheet" class="row g-3 mt-2">
              <div class="col-6 col-md-3">
                <div class="summary-label">
                  {{ $t("timesheets.pendingHours") }}
                </div>
                <div class="summary-value">
                  {{ week.timesheet.pending_hours }}{{ $t("timesheets.hours") }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-label">
                  {{ $t("timesheets.approvedHours") }}
                </div>
                <div class="summary-value">
                  {{ week.timesheet.approved_hours }}{{ $t("timesheets.hours") }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-label">{{ $t("timesheets.paidHours") }}</div>
                <div class="summary-value">
                  {{ week.timesheet.paid_hours }}{{ $t("timesheets.hours") }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-label">{{ $t("timesheets.totalAmount") }}</div>
                <div class="summary-value">
                  {{ formatCurrency(week.totalHours * payRate) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ================= History ================= -->
        <div class="card border-0 shadow-sm">
          <div
            class="card-header bg-white d-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0">{{ $t("employee.timesheets.history") }}</h5>
            <select
              v-model="statusFilter"
              class="form-select form-select-sm w-auto"
              @change="reloadHistory"
            >
              <option value="all">{{ $t("timesheets.allStatuses") }}</option>
              <option value="draft">{{ $t("timesheets.draft") }}</option>
              <option value="pending">{{ $t("timesheets.pending") }}</option>
              <option value="approved">{{ $t("timesheets.approved") }}</option>
              <option value="rejected">{{ $t("timesheets.rejected") }}</option>
              <option value="paid">{{ $t("timesheets.paid") }}</option>
            </select>
          </div>

          <div class="card-body">
            <div v-if="loading" class="text-center py-4 text-muted">
              {{ $t("common.loading") }}
            </div>

            <div v-else-if="timesheets.length === 0" class="text-center py-4 text-muted">
              {{ $t("employee.timesheets.noHistory") }}
            </div>

            <div v-else class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>{{ $t("timesheets.week") }}</th>
                    <th class="text-end">{{ $t("timesheets.totalHours") }}</th>
                    <th class="text-end">{{ $t("timesheets.totalAmount") }}</th>
                    <th>{{ $t("timesheets.status") }}</th>
                    <th class="text-end">{{ $t("common.actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="timesheet in timesheets" :key="timesheet.id">
                    <td>
                      {{ formatWeek(timesheet.week_start_date, timesheet.week_end_date) }}
                    </td>
                    <td class="text-end">
                      {{ timesheet.total_hours }}{{ $t("timesheets.hours") }}
                    </td>
                    <td class="text-end">
                      {{
                        formatCurrency(
                          timesheet.total_hours * (timesheet.pay_rate || payRate),
                        )
                      }}
                    </td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(timesheet.status)">
                        {{ $t("timesheets." + timesheet.status) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="goToWeek(timesheet.week_start_date)"
                      >
                        {{ $t("common.view") }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <nav v-if="pagination.totalPages > 1" class="mt-3">
              <ul class="pagination pagination-sm justify-content-center mb-0">
                <li
                  class="page-item"
                  :class="{ disabled: pagination.currentPage === 1 }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(pagination.currentPage - 1)"
                  >
                    {{ $t("common.previous") }}
                  </a>
                </li>
                <li
                  v-for="page in pagination.totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: page === pagination.currentPage }"
                >
                  <a class="page-link" href="#" @click.prevent="changePage(page)">
                    {{ page }}
                  </a>
                </li>
                <li
                  class="page-item"
                  :class="{
                    disabled: pagination.currentPage === pagination.totalPages,
                  }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(pagination.currentPage + 1)"
                  >
                    {{ $t("common.next") }}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Single-day entry modal, shared by add / edit / resubmit -->
    <DayEntryModal
      v-if="dayForm.open"
      :date="dayForm.date"
      :hours="dayForm.hours"
      :notes="dayForm.notes"
      @update:date="dayForm.date = $event"
      @update:hours="dayForm.hours = $event"
      @update:notes="dayForm.notes = $event"
      :is-edit="!!dayForm.id"
      :rejection-reason="dayForm.rejectionReason"
      :pay-rate="payRate"
      :saving="dayForm.saving"
      :error="dayForm.error"
      @save="saveDay"
      @close="closeDayForm"
    />
  </div>
</template>

<script>
import axios from "axios";
import EmployeeSidebar from "@/components/EmployeeSidebar.vue";
import EmployeeNavbar from "@/components/EmployeeNavbar.vue";
import DayEntryModal from "@/components/DayEntryModal.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

// Editable statuses: everything else is under review or already settled
const EDITABLE = ["draft", "rejected"];

export default {
  name: "EmployeeTimesheets",
  components: {
    EmployeeSidebar,
    EmployeeNavbar,
    DayEntryModal,
  },
  data() {
    return {
      sidebarOpen: true,
      user: { name: "", email: "", role: "employee" },
      banner: null,
      statusFilter: "all",

      // The week currently on screen
      weekDate: this.todayIso(),
      week: { weekStart: "", weekEnd: "", days: [], totalHours: 0, timesheet: null },
      loadingWeek: false,

      dayForm: {
        open: false,
        id: null,
        date: "",
        hours: "",
        notes: "",
        rejectionReason: null,
        saving: false,
        error: null,
      },

      timesheets: [],
      stats: {
        draft: 0,
        pending: { count: 0, hours: 0 },
        approved: { count: 0, hours: 0, amount: 0 },
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      },
      payRate: 0,
      loading: false,
    };
  },
  computed: {
    draftCount() {
      return this.stats.draft || 0;
    },
    pendingCount() {
      return this.stats.pending.count || 0;
    },
    pendingHours() {
      return this.stats.pending.hours || 0;
    },
    approvedHours() {
      return this.stats.approved.hours || 0;
    },
    approvedAmount() {
      return this.stats.approved.amount || 0;
    },
    isCurrentWeek() {
      const today = this.todayIso();
      return (
        !!this.week.weekStart &&
        today >= this.week.weekStart &&
        today <= this.week.weekEnd
      );
    },
  },
  async mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    this.user = JSON.parse(userData);
    if (this.user.role === "admin") {
      this.$router.push("/dashboard");
      return;
    }

    await this.loadEmployeeProfile();
    await Promise.all([this.loadWeek(), this.loadStatistics(), this.loadTimesheets()]);
  },
  methods: {
    // ---------- helpers ----------
    todayIso() {
      return new Date().toISOString().slice(0, 10);
    },
    authHeaders() {
      return {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };
    },
    notify(type, message) {
      this.banner = { type, message };
      if (type === "success") {
        setTimeout(() => {
          this.banner = null;
        }, 4000);
      }
    },
    isEditable(entry) {
      return EDITABLE.includes(entry.status);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
    formatWeek(start, end) {
      if (!start || !end) return "-";
      return `${this.formatDateEU(start)} - ${this.formatDateEU(end)}`;
    },
    formatDateEU(dateString) {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleDateString("en-GB");
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount || 0);
    },
    getStatusBadgeClass(status) {
      return (
        {
          draft: "bg-secondary",
          pending: "bg-warning",
          approved: "bg-success",
          rejected: "bg-danger",
          paid: "bg-primary",
        }[status] || "bg-secondary"
      );
    },

    // ---------- week ----------
    async loadWeek() {
      try {
        this.loadingWeek = true;
        const response = await axios.get(`${API_URL}/api/timesheets/week`, {
          ...this.authHeaders(),
          params: { date: this.weekDate },
        });
        if (response.data.success) {
          this.week = response.data.data;
          if (response.data.data.payRate !== null) {
            this.payRate = response.data.data.payRate;
          }
        }
      } catch (error) {
        this.notify(
          "danger",
          error.response?.data?.message || "Failed to load week",
        );
      } finally {
        this.loadingWeek = false;
      }
    },
    shiftWeek(direction) {
      const date = new Date(`${this.week.weekStart}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + direction * 7);
      this.weekDate = date.toISOString().slice(0, 10);
      this.loadWeek();
    },
    goToWeek(weekStart) {
      this.weekDate = String(weekStart).slice(0, 10);
      this.loadWeek();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // ---------- day form ----------
    openNewDay(date) {
      // Default to today when it falls in the week on screen, else the week start
      const fallback = this.isCurrentWeek ? this.todayIso() : this.week.weekStart;
      this.dayForm = {
        open: true,
        id: null,
        date: date || fallback,
        hours: "",
        notes: "",
        rejectionReason: null,
        saving: false,
        error: null,
      };
    },
    openEditDay(entry) {
      this.dayForm = {
        open: true,
        id: entry.id,
        date: entry.work_date,
        hours: entry.hours,
        notes: entry.notes || "",
        rejectionReason: entry.rejection_reason,
        saving: false,
        error: null,
      };
    },
    closeDayForm() {
      this.dayForm.open = false;
    },
    async saveDay(submit) {
      if (this.dayForm.saving) return; // guards against a double-click
      this.dayForm.saving = true;
      this.dayForm.error = null;

      const body = {
        hours: parseFloat(this.dayForm.hours) || 0,
        notes: this.dayForm.notes,
        submit,
      };

      try {
        let message;
        if (this.dayForm.id) {
          await axios.put(
            `${API_URL}/api/timesheets/day/${this.dayForm.id}`,
            body,
            this.authHeaders(),
          );
          message = submit
            ? this.dayForm.rejectionReason
              ? this.$t("employee.timesheets.dayResubmitted")
              : this.$t("employee.timesheets.daySubmitted")
            : this.$t("employee.timesheets.dayUpdated");
        } else {
          await axios.post(
            `${API_URL}/api/timesheets/day`,
            { ...body, workDate: this.dayForm.date },
            this.authHeaders(),
          );
          message = submit
            ? this.$t("employee.timesheets.daySubmitted")
            : this.$t("employee.timesheets.dayDraftSaved");
        }

        this.closeDayForm();
        this.notify("success", message);
        await this.refreshAll();
      } catch (error) {
        this.dayForm.error =
          error.response?.data?.message || "Failed to save hours";
      } finally {
        this.dayForm.saving = false;
      }
    },
    async deleteDay(entry) {
      const confirmed = window.confirm(
        this.$t("employee.timesheets.deleteConfirmation", {
          date: this.formatDateEU(entry.work_date),
        }),
      );
      if (!confirmed) return;

      try {
        await axios.delete(
          `${API_URL}/api/timesheets/day/${entry.id}`,
          this.authHeaders(),
        );
        this.notify("success", this.$t("employee.timesheets.dayDeleted"));
        await this.refreshAll();
      } catch (error) {
        this.notify(
          "danger",
          error.response?.data?.message || "Failed to delete entry",
        );
      }
    },
    async refreshAll() {
      await Promise.all([
        this.loadWeek(),
        this.loadStatistics(),
        this.loadTimesheets(),
      ]);
    },

    // ---------- history & stats ----------
    async loadTimesheets() {
      try {
        this.loading = true;
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
        };
        if (this.statusFilter !== "all") params.status = this.statusFilter;

        const response = await axios.get(`${API_URL}/api/timesheets`, {
          ...this.authHeaders(),
          params,
        });

        if (response.data.success) {
          this.timesheets = response.data.data;
          this.pagination.totalItems = response.data.pagination.total;
          this.pagination.totalPages = response.data.pagination.totalPages;
        }
      } catch (error) {
        this.notify(
          "danger",
          error.response?.data?.message || "Failed to load timesheets",
        );
      } finally {
        this.loading = false;
      }
    },
    reloadHistory() {
      this.pagination.currentPage = 1;
      this.loadTimesheets();
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.currentPage = page;
      this.loadTimesheets();
    },
    async loadEmployeeProfile() {
      try {
        const response = await axios.get(
          `${API_URL}/api/employee/profile`,
          this.authHeaders(),
        );
        if (response.data.success) {
          this.payRate = parseFloat(response.data.data.pay_rate) || 0;
        }
      } catch (error) {
        console.error("Error loading employee profile:", error);
      }
    },
    async loadStatistics() {
      try {
        const response = await axios.get(
          `${API_URL}/api/timesheets/stats`,
          this.authHeaders(),
        );
        if (response.data.success) {
          const data = response.data.data;
          this.stats = {
            draft: data.draftCount,
            pending: { count: data.pendingCount, hours: data.pendingHours },
            approved: {
              count: data.approvedCount,
              hours: data.approvedHours,
              amount: data.approvedAmount,
            },
          };
        }
      } catch (error) {
        console.error("Error loading statistics:", error);
      }
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.main-content {
  margin-left: 280px;
  transition: all 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.stat-card {
  border-radius: 12px;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.03em;
}

.summary-value {
  font-weight: 600;
  font-size: 1.05rem;
}

.table thead th {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 600;
  letter-spacing: 0.03em;
}

@media (max-width: 768px) {
  .main-content,
  .main-content.sidebar-collapsed {
    margin-left: 0;
  }
}
</style>
